import {
  CreateButton,
  Datagrid,
  FilterButton,
  FunctionField,
  List,
  TextField,
  TextInput,
  TopToolbar,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { marketsForScope } from '../dataProvider/types';
import type { SeoRule } from '../dataProvider';

/**
 * Per-page SEO meta rules list (D-cms-testimonials-seo). A FLAT structured surface, NO
 * rich text and NO publish lifecycle. Each rule keys to a FREE pageKey / path (NOT a FK),
 * so the SAME list spans cmsPage slugs, blogPost paths, pageSchema pageIds and system
 * routes uniformly. OPERATIONAL market-scoped: the switcher governs which rules show (the
 * active markets PLUS the all-market ones). The region variant IS the market: the same
 * pageKey can appear as a uk row and a us row. ROLE-gated to editor and super_admin;
 * authoring gates cms:author.
 */

const seoFilters = [
  <TextInput key="q" source="q" label="Search page key" alwaysOn resettable />,
];

const SeoRuleListActions = () => {
  const { role, accessibleRegions } = useAccessContext();
  const canAuthor = hasAccess(
    { role, accessibleRegions },
    { capability: 'cms:author' },
  );
  return (
    <TopToolbar>
      <FilterButton />
      {canAuthor && <CreateButton label="New SEO rule" />}
    </TopToolbar>
  );
};

/** The list itself. Exported for testing without the access wrapper. */
export const SeoRuleListView = () => {
  const { activeRegion } = useRegion();
  // The active markets PLUS the all-market rows (a both row shows in every scope).
  const markets = [...marketsForScope(activeRegion ?? 'GLOBAL'), 'all'];
  return (
    <List
      resource="seoRules"
      title="SEO rules"
      filter={{ market: markets }}
      filters={seoFilters}
      actions={<SeoRuleListActions />}
      sort={{ field: 'pageKey', order: 'ASC' }}
      perPage={25}
      exporter={false}
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="pageKey" label="Page key" />
        <TextField source="metaTitle" label="Meta title" />
        <FunctionField
          label="Market"
          render={(r: SeoRule) => String(r.market).toUpperCase()}
        />
      </Datagrid>
    </List>
  );
};

/** Gated entry point. ROLE-gated to editor and super_admin. */
export const SeoRuleList = () => (
  <RequireAccess roles={['editor', 'super_admin']}>
    <SeoRuleListView />
  </RequireAccess>
);
